!<template>
  <card-background class="content" ref="contentContainer">
    <div v-if="!isLoading" class="content__header" :ref="(el) => observeHeader(el)">
      <kaz-breadcrumbs :links="updateBreadcrumbLinks" :state="LOADING_STATES.LOADED" />
      <h1 class="heading heading_size_h1 main-title">
        {{ getMainTitle }}
      </h1>
      <nav v-if="currentMode !== MODE.BOTH">
        <ol class="navigation-container">
          <template v-for="(entity, id) in bookEntities">
            <li v-if="entity.books.length" :key="id">
              <a
                class="link-button"
                :class="{ 'link-button_active' : selectedEntityIds?.has(id)}"
                :href="'#' + id"
                @click.prevent="gotoContentEntity(id)"
              >
                {{ entity.title }}
              </a>
            </li>
          </template>
        </ol>
      </nav>
    </div>
    <KazLoader v-if="isLoading" size="L" />
    <div
      v-else
      :class="currentMode === MODE.SUBJECT ? 'content__layout_two-columns' : 'content__layout_one-columns'"
    >
      <template v-for="(entity, id) in bookEntities">
        <div 
          v-if="entity.books.length"
          class="content__entity" 
          :key="id"
          :ref="(el) => fillEntitiesRefs(id, el)"
          :id="id"
        >
          <h3 class="heading heading_size_h3" v-if="entity.title">
            {{ entity.title }}
          </h3>
          <div
            v-if="currentMode !== MODE.BOTH"
            :class="[
              entity.books.length < 5 && screenWidth >= 568 ? 'books-flex' : 'books-grid',
              'books-grid_limited'
            ]"
          >
            <book-card
              v-for="book in entity.books"
              :key="book.id"
              :id="book.id"
              :bookTitle="getBookTitle(id)"
              :bookImg="book.bookImg"
              :bookDescription="book.bookDescription"
              :authors="book.authors.slice(0, 2)"
            />
          </div>
          <template v-else>
            <section
              v-if="filterBooksByRank.filledBooks.length"
              class="ranked-section"
            >
              <header class="ranked-section__header">
                <div class="ranked-section__title">
                  <h2 class="heading heading_size_h2">
                    Пособия с заполненными решениями
                  </h2>
                </div>
                <p class="body body_size_M ranked-section__desc">
                  Собрали все учебники, рабочие тетради и прочее, где заполнены все решения
                </p>
              </header>
              <div
                :class="[
                  filterBooksByRank.filledBooks.length < 5 && screenWidth >= 568 ? 'books-flex' : 'books-grid'
                ]"
              >
                <book-card
                  v-for="book in filterBooksByRank.filledBooks"
                  :key="book.id"
                  :id="book.id"
                  :bookTitle="getBookTitle(id)"
                  :bookImg="book.bookImg"
                  :bookDescription="book.bookDescription"
                  :authors="book.authors.slice(0, 2)"
                />
              </div>
            </section>
            <section 
              v-if="filterBooksByRank.emptyBooks.length" 
              class="ranked-section"
            >
              <header class="ranked-section__header">
                <div class="ranked-section__title">
                  <h2 class="heading heading_size_h2">
                    Пособия 
                    <span style="color: var(--kaz-base-accent)">
                      без заполненых решений
                    </span>
                  </h2>
                </div>
                <p class="body body_size_M ranked-section__desc">
                  Нет, это не ошибка. Хочешь заработать и помочь тем, кто ищет решения из этих пособий - заполняй недостающие решения
                </p>
                <KazLink
                  label="Нажми, чтобы узнать как заработать на заполнении решений"
                  leftIconName="kuiIconInfo"
                  :leftIcon="true"
                  :underline="true"
                  :programNavigation="openMoneyManual"
                />
              </header>
              <div
                :class="[
                  filterBooksByRank.emptyBooks.length < 5 && screenWidth >= 568 ? 'books-flex' : 'books-grid'
                ]"
              >
                <book-card
                  v-for="book in filterBooksByRank.emptyBooks.slice(0,emptyBooksNumber)"
                  :key="book.id"
                  :id="book.id"
                  :bookTitle="getBookTitle(id)"
                  :bookImg="book.bookImg"
                  :bookDescription="book.bookDescription"
                  :authors="book.authors.slice(0, 2)"
                />
              </div>
              <KazButton
                v-if="emptyBooksNumberLimit < filterBooksByRank.emptyBooks.length"
                class="empty-books-toggler"
                :label="emptyBooksNumber < filterBooksByRank.emptyBooks.length
                  ? `Показать еще ${filterBooksByRank.emptyBooks.length - emptyBooksNumber}`
                  : 'Скрыть учебники'"
                size="L"
                appearance="secondary"
                mode="onLight"
                :centerContent="true"
                style="margin-top: 16px;"
                @click="toggleEmptyBookList(filterBooksByRank.emptyBooks.length)"
              />
            </section>
          </template>
          <KazButton
            v-if="currentMode !== MODE.BOTH"
            :label="'Перейти ко всем учебникам'"
            :size="'M'"
            :appearance="'secondary'"
            :mode="'normal'"
            @click="goToAllBooks(id)"
          />
        </div>
      </template>
    </div>
  </card-background>
</template>

<script>
import CardBackground from '@/components/UI/card/CardBackground.vue';
import KazBreadcrumbs from '@/components/KazUI/molecules/breadcrumbs';
import KazLoader from '@/components/KazUI/atoms/loader';
import BookCard from '@/components/cards/BookCard';
import KazLink from '@/components/KazUI/atoms/linkElement';
import KazButton from '@/components/KazUI/atoms/button';

import useBooksList from '@/hooks/books/useBooksList';
import { MODE as BOOKS_MODE } from '@/hooks/books/useBooksList';


export default {
  name: 'SubjectBooks',

  components: {
    CardBackground,
    KazBreadcrumbs,
    KazLoader,
    BookCard,
    KazLink,
    KazButton,
  },

  setup() {
    const {
      MODE,
      LOADING_STATES,
      observeHeader,

      currentMode,
      subjectName,
      levelName,
      isLoading,
      bookEntities,
      selectedEntityIds,
      screenWidth,
      emptyBooksNumber,
      emptyBooksNumberLimit,

      loadingStatesFull,
      updateBreadcrumbLinks,
      getMainTitle,
      filterBooksByRank,

      prepareComponent,
      fillEntitiesRefs,
      getBookTitle,
      openMoneyManual,
      toggleEmptyBookList,
      gotoContentEntity,
      goToAllBooks,
    } = useBooksList({ mode: BOOKS_MODE.SUBJECT });

    return {
      MODE,
      LOADING_STATES,
      observeHeader,

      currentMode,
      subjectName,
      levelName,
      isLoading,
      bookEntities,
      selectedEntityIds,
      screenWidth,
      emptyBooksNumber,
      emptyBooksNumberLimit,

      loadingStatesFull,
      updateBreadcrumbLinks,
      getMainTitle,
      filterBooksByRank,

      prepareComponent,
      fillEntitiesRefs,
      getBookTitle,
      openMoneyManual,
      toggleEmptyBookList,
      gotoContentEntity,
      goToAllBooks,
    };
  },


  watch: {
    loadingStatesFull: {
      async handler(states) {
        const isDataReady = states.gradeState === this.LOADING_STATES.LOADED && states.courseState === this.LOADING_STATES.LOADED;
        const newSubject = states.routeParams.subject ?? null;
        if (isDataReady && newSubject) {
          try {
            this.subjectName = newSubject;
            await this.prepareComponent(); 
          } catch (error) {
            console.error(error);
          }
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
@import './style/index.css';
</style>
